/**
 * Live Firestore reads, with the states the interface actually needs.
 *
 * Two rules this layer exists to enforce:
 *
 *   • Every read is normalised on the way in. Components receive `Date`, never
 *     `Timestamp`, and `null`, never a missing field. Nothing downstream has
 *     to defend itself.
 *
 *   • A read has four outcomes, not two. Loading, empty, error, and *stale* —
 *     data from the on-disk cache while the network is unreachable. Collapsing
 *     stale into loading is what makes offline apps feel broken; the app shows
 *     the cached content and says so.
 *
 * Listeners are torn down by the effect that made them, and every query is
 * memoised by a caller-supplied key so a re-render does not detach and
 * reattach the subscription — the classic source of read-count blowouts.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  onSnapshot,
  type DocumentData,
  type DocumentReference,
  type Query,
  type QuerySnapshot,
  type DocumentSnapshot,
  type FirestoreError,
} from 'firebase/firestore';

export interface AsyncData<T> {
  data: T;
  loading: boolean;
  error: FirestoreError | null;
  /** True when the values came from cache and the server has not confirmed. */
  stale: boolean;
}

/**
 * Subscribe to a query.
 *
 * `key` identifies the query for memoisation. It must change exactly when the
 * query does — passing an inline query object without a stable key would
 * resubscribe on every render.
 */
export function useCollection<T>(
  build: () => Query<DocumentData> | null,
  map: (snapshot: QuerySnapshot<DocumentData>) => T[],
  key: string
): AsyncData<T[]> {
  const [state, setState] = useState<AsyncData<T[]>>({
    data: [],
    loading: true,
    error: null,
    stale: false,
  });

  // Keep the latest mapper without making it a dependency; an inline function
  // would otherwise resubscribe every render.
  const mapRef = useRef(map);
  mapRef.current = map;

  const buildRef = useRef(build);
  buildRef.current = build;

  useEffect(() => {
    const query = buildRef.current();
    if (!query) {
      setState({ data: [], loading: false, error: null, stale: false });
      return;
    }

    setState((prev) => ({ ...prev, loading: true, error: null }));

    return onSnapshot(
      query,
      { includeMetadataChanges: true },
      (snapshot) => {
        setState({
          data: mapRef.current(snapshot),
          loading: false,
          error: null,
          stale: snapshot.metadata.fromCache,
        });
      },
      (error) => {
        setState((prev) => ({ ...prev, loading: false, error: tagSource(error, key) }));
      }
    );
  }, [key]);

  return state;
}

/**
 * Attach the query key to the error.
 *
 * With a dozen screens rendering the same "not available to your account"
 * message, an error with no provenance is close to undiagnosable — especially
 * on a phone, where there is no console to open. The key names the exact read
 * that failed, and the interface shows it quietly. It costs nothing and it is
 * the difference between fixing a report and guessing at one.
 */
function tagSource(error: FirestoreError, key: string): FirestoreError {
  return Object.assign(error, { source: key });
}

/** Subscribe to a single document. `null` data means it does not exist. */
export function useDocument<T>(
  build: () => DocumentReference<DocumentData> | null,
  map: (snapshot: DocumentSnapshot<DocumentData>) => T | null,
  key: string
): AsyncData<T | null> {
  const [state, setState] = useState<AsyncData<T | null>>({
    data: null,
    loading: true,
    error: null,
    stale: false,
  });

  const mapRef = useRef(map);
  mapRef.current = map;
  const buildRef = useRef(build);
  buildRef.current = build;

  useEffect(() => {
    const ref = buildRef.current();
    if (!ref) {
      setState({ data: null, loading: false, error: null, stale: false });
      return;
    }

    setState((prev) => ({ ...prev, loading: true, error: null }));

    return onSnapshot(
      ref,
      { includeMetadataChanges: true },
      (snapshot) => {
        setState({
          data: snapshot.exists() ? mapRef.current(snapshot) : null,
          loading: false,
          error: null,
          stale: snapshot.metadata.fromCache,
        });
      },
      (error) => {
        setState((prev) => ({ ...prev, loading: false, error: tagSource(error, key) }));
      }
    );
  }, [key]);

  return state;
}

/**
 * Turn a Firestore error into something a person can act on.
 *
 * `permission-denied` is the interesting one: in this app it almost always
 * means the semester is outside its dates or the reader is not enrolled, which
 * is a correct outcome rather than a fault, and saying "insufficient
 * permissions" would be both alarming and unhelpful.
 */
export function describeError(error: FirestoreError | Error | null): string {
  if (!error) return '';
  const code = (error as FirestoreError).code;
  switch (code) {
    case 'permission-denied':
      return 'This content is not available to your account right now.';
    case 'unavailable':
      return "Can't reach the server. Showing what was saved on this device.";
    case 'failed-precondition':
      return 'This view needs a database index that has not finished building yet.';
    case 'not-found':
      return 'That item no longer exists.';
    case 'unauthenticated':
      return 'Your session expired. Sign in again to continue.';
    default:
      return error.message || 'Something went wrong.';
  }
}

/** Stable key helper, so callers stop hand-rolling template strings. */
export function queryKey(...parts: Array<string | number | boolean | null | undefined>): string {
  return parts.map((p) => (p == null ? '~' : String(p))).join('|');
}

/** Memoise a value by a key, for callers building query objects. */
export function useStable<T>(factory: () => T, key: string): T {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(factory, [key]);
}
